const API_URL = process.env.REACT_APP_API_URL;

/**
 * Upload multiple images to the server
 * @param {File[]} files - Array of image files to upload
 * @param {string} token - Firebase ID token for authentication
 * @param {function} onProgress - Callback function for upload progress (0-100)
 * @returns {Promise<Array>} - Array of upload results
 */
export const uploadImages = async (files, token, onProgress = () => {}) => {
  if (!API_URL) {
    throw new Error('API_URL is not configured');
  }

  if (!files || files.length === 0) {
    throw new Error('No files provided');
  }

  if (!token) {
    throw new Error('Authentication token is required');
  }

  try {
    console.log('uploadImages: Starting upload of', files.length, 'files');
    
    // Create FormData for multipart upload
    const formData = new FormData();
    
    // Append all files to FormData
    files.forEach((file, index) => {
      formData.append('images', file);
      console.log(`uploadImages: Added file ${index + 1}:`, file.name, file.type, file.size);
    });

    console.log('uploadImages: Making request to:', `${API_URL}/images/upload`);

    // Create XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          console.log('Upload progress:', progress + '%');
          onProgress(progress);
        }
      });

      // Handle completion
      xhr.addEventListener('load', () => {
        console.log('uploadImages: Response received');
        console.log('uploadImages: Response status:', xhr.status);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const result = JSON.parse(xhr.responseText);
            console.log('uploadImages: Success response:', result);
            
            // Check if response has files array (new format) or success property (old format)
            if (result.files && Array.isArray(result.files)) {
              // New response format with files array
              const results = result.files.map((file) => ({
                filename: file.originalName,
                success: true,
                error: null,
                fileId: file.id
              }));
              resolve(results);
            } else if (result.success) {
              // Old response format with success property
              const results = files.map((file, index) => ({
                filename: file.name,
                success: true,
                error: null
              }));
              resolve(results);
            } else {
              // Response doesn't match expected format
              throw new Error(result.message || 'Upload failed - unexpected response format');
            }
          } catch (parseError) {
            console.error('uploadImages: Failed to parse response:', parseError);
            reject(new Error('Invalid response from server'));
          }
        } else {
          console.error('uploadImages: HTTP error:', xhr.status, xhr.statusText);
          try {
            const errorResult = JSON.parse(xhr.responseText);
            reject(new Error(errorResult.message || `Upload failed: ${xhr.status} ${xhr.statusText}`));
          } catch (parseError) {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        console.error('uploadImages: Network error');
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('timeout', () => {
        console.error('uploadImages: Request timeout');
        reject(new Error('Upload request timed out'));
      });

      // Set timeout (5 minutes for large uploads)
      xhr.timeout = 5 * 60 * 1000;

      // Prepare and send request
      xhr.open('POST', `${API_URL}/images/upload`);
      
      // Set authentication header
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      console.log('uploadImages: Sending request...');
      xhr.send(formData);
    });

  } catch (error) {
    console.error('uploadImages error:', error);
    throw error;
  }
};
